import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Reminder, ReminderFormData } from '@/types/crm';

interface ReminderStore {
  reminders: Reminder[];
  isLoading: boolean;
  fetchRemindersByLead: (leadId: string) => Promise<Reminder[]>;
  fetchRemindersByDeal: (dealId: string) => Promise<Reminder[]>;
  createReminder: (data: ReminderFormData) => Promise<Reminder | null>;
  updateReminder: (id: string, data: Partial<ReminderFormData>) => Promise<void>;
  deleteReminder: (id: string) => Promise<void>;
  toggleReminderStatus: (id: string, currentStatus: string) => Promise<void>;
}

export const useReminderStore = create<ReminderStore>((set, get) => ({
  reminders: [],
  isLoading: false,

  fetchRemindersByLead: async (leadId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('crm_reminders')
        .select('*')
        .eq('lead_id', leadId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      set({ reminders: data || [] });
      return data || [];
    } finally {
      set({ isLoading: false });
    }
  },

  fetchRemindersByDeal: async (dealId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('crm_reminders')
        .select('*')
        .eq('deal_id', dealId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      set({ reminders: data || [] });
      return data || [];
    } finally {
      set({ isLoading: false });
    }
  },

  createReminder: async (data: ReminderFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: reminder, error } = await supabase
      .from('crm_reminders')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    if (data.lead_id) {
      await get().fetchRemindersByLead(data.lead_id);
    } else if (data.deal_id) {
      await get().fetchRemindersByDeal(data.deal_id);
    }
    return reminder;
  },

  updateReminder: async (id: string, data: Partial<ReminderFormData>) => {
    const { error } = await supabase
      .from('crm_reminders')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    
    const { reminders } = get();
    if (reminders.length > 0) {
      if (reminders[0].lead_id) {
        await get().fetchRemindersByLead(reminders[0].lead_id);
      } else if (reminders[0].deal_id) {
        await get().fetchRemindersByDeal(reminders[0].deal_id);
      }
    }
  },

  deleteReminder: async (id: string) => {
    const { reminders } = get();
    const reminder = reminders.find(r => r.id === id);
    
    const { error } = await supabase
      .from('crm_reminders')
      .delete()
      .eq('id', id);

    if (error) throw error;
    
    if (reminder?.lead_id) {
      await get().fetchRemindersByLead(reminder.lead_id);
    } else if (reminder?.deal_id) {
      await get().fetchRemindersByDeal(reminder.deal_id);
    }
  },

  toggleReminderStatus: async (id: string, currentStatus: string) => {
    const newStatus = currentStatus === 'pending' ? 'done' : 'pending';
    const { error } = await supabase
      .from('crm_reminders')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    
    const { reminders } = get();
    if (reminders.length > 0) {
      if (reminders[0].lead_id) {
        await get().fetchRemindersByLead(reminders[0].lead_id);
      } else if (reminders[0].deal_id) {
        await get().fetchRemindersByDeal(reminders[0].deal_id);
      }
    }
  },
}));
